import type { CountdownApiClient } from "@countdown/api-client";
import {
  type DoorOutcome,
  type DoorWrites,
  type FullDoorLayout,
  SAMPLE_DOOR,
  fitLayout,
  fullLayout,
  layoutRequest,
  sameLayout,
} from "@countdown/door-editor";
import type { DoorPresentation } from "@countdown/theme";
import {
  type ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { DoorEditor } from "@/components/DoorEditor";
import { HelpTip } from "@/components/HelpTip";
import { announceRefusal, type SaveResult, useSaveSection } from "@/components/SaveBar";
import { Button } from "@/components/ui/button";
import {
  type ActionDetail,
  type ExampleEdit,
  type FallbackOutcome,
  assetAddress,
  exampleDoor,
  fallbackDoor,
  loadExample,
  originalAddress,
  removeFallbackImage,
  saveDoorWindow,
  saveFallbackText,
  uploadFallbackImage,
  useResource,
} from "@/lib/api";
import { HELP_TIPS } from "@/lib/help";
import { navLinkClassName } from "@/lib/utils";

/** The door window of an action as the settings hold it until "Speichern" (D37). */
export interface DoorWindowDraft {
  readonly presentation: DoorPresentation;
  readonly layout: FullDoorLayout;
  readonly sponsorEditable: boolean;
}

export function doorWindowOf(detail: ActionDetail): DoorWindowDraft {
  const presentation = detail.door_presentation ?? "page";
  return {
    presentation,
    layout: fitLayout(fullLayout(detail.door_layout), presentation),
    sponsorEditable: detail.door_layout_sponsor_editable ?? false,
  };
}

export function sameDoorWindow(left: DoorWindowDraft, right: DoorWindowDraft): boolean {
  return (
    left.presentation === right.presentation &&
    left.sponsorEditable === right.sponsorEditable &&
    sameLayout(left.layout, right.layout)
  );
}

const PRESENTATIONS: readonly (readonly [DoorPresentation, string])[] = [
  ["page", "Eigene Seite"],
  ["dialog", "Fenster über dem Kalender"],
];

/**
 * How the doors of this action open (D37): as a page or as a window over the calendar, the
 * standard layout of that window, and whether sponsors may change the layout of their door.
 * The presentation is the publisher's alone; no sponsor sees or sets it.
 *
 * The layout is configured on the open door of the action's example view, in the same
 * configurator the doors themselves use. Until that door has slides, or for a role the example
 * is closed to, a door made up for the purpose (`SAMPLE_DOOR`) stands in. A switch to the window over the calendar fits every
 * format to the ones it offers at once, as the server would. One save, in the tab's bar.
 *
 * »Unbelegtes Türchen« shows the action's fallback content in the same window (D88): its text
 * and picture, written by the element `action_fallback`, with a save of its own in the bar.
 * Until 23.10.2026 the text was a form of the settings tab, and the picture had no writer.
 */
export function DoorWindowSettings({
  detail,
  draft,
  onChange,
  readOnly,
  sponsorWorkflow,
  onSaved,
  fallbackReadOnly = readOnly,
  mayAcceptUnscanned = false,
  showUnbooked = false,
}: {
  readonly detail: ActionDetail;
  readonly draft: DoorWindowDraft;
  readonly onChange: (next: DoorWindowDraft) => void;
  readonly readOnly: boolean;
  /** Whether the sponsors' switch shows: part of »Pflege durch Sponsoren« (D57). Never in a
   * customer's calendar, whose customer keeps to the content (E5). */
  readonly sponsorWorkflow: boolean;
  readonly onSaved: () => void;
  /** Whether the fallback content is only shown: without the element `action_fallback`. */
  readonly fallbackReadOnly?: boolean;
  readonly mayAcceptUnscanned?: boolean;
  /** Opens with the unbooked door, e.g. from the go-live obstacle »Ersatzinhalt fehlt«. */
  readonly showUnbooked?: boolean;
}): ReactElement {
  const id = useId();
  const [unbooked, setUnbooked] = useState(showUnbooked);
  // Mounted on its first visit and kept, so its draft survives a switch and the bar saves it.
  const [unbookedSeen, setUnbookedSeen] = useState(showUnbooked);
  // A link asking for the unbooked door while the view stands (the header's obstacle) opens it.
  const [asked, setAsked] = useState(showUnbooked);
  if (showUnbooked !== asked) {
    setAsked(showUnbooked);
    if (showUnbooked) {
      setUnbooked(true);
      setUnbookedSeen(true);
    }
  }
  const rootRef = useRef<HTMLDivElement>(null);
  const fallbackRef = useRef<HTMLDivElement>(null);
  /** A save of the fallback that stopped or was refused: back in sight, view and door. */
  const showFallback = useCallback((): void => {
    setUnbooked(true);
    announceRefusal(fallbackRef.current);
  }, []);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = !sameDoorWindow(draft, doorWindowOf(detail));
  const loadPreview = useCallback(
    (api: CountdownApiClient) => loadExample(api, detail.action_id),
    [detail.action_id],
  );
  const example = useResource<ExampleEdit>(loadPreview);
  // One copy per answer: the configurator takes every new copy as a stand saved elsewhere.
  const exampleOpen = useMemo(() => {
    if (example.status !== "ready") {
      return null;
    }
    const door = exampleDoor(example.data);
    return door.slides.length > 0 ? door : null;
  }, [example]);

  async function save(): Promise<SaveResult> {
    setBusy(true);
    setNotice(null);
    try {
      // The built-in layout is saved as none (NULL), so the action keeps following it: a frozen
      // copy would not, and the style transfer would spread the copy. The API sends a stored
      // NULL as the built-in layout, so equal to it is all the page can tell.
      const builtIn = sameLayout(draft.layout, fitLayout(fullLayout(null), draft.presentation));
      const outcome = await saveDoorWindow(detail.action_id, {
        presentation: draft.presentation,
        layout: builtIn ? null : layoutRequest(draft.layout),
        sponsor_editable: draft.sponsorEditable,
      });
      if (!outcome.ok) {
        setNotice(outcome.message);
        announceRefusal(rootRef.current);
        return "failed";
      }
      console.log("[DoorWindowSettings] saved", detail.action_id);
      onSaved();
      return "ok";
    } finally {
      setBusy(false);
    }
  }

  const inBar = useSaveSection(readOnly ? null : { label: "Türchen-Fenster", dirty, save });

  return (
    <div ref={rootRef}>
      {/* Named by the legend's text alone: the "?" inside would add "Hilfe zu Darstellung". */}
      <fieldset className="min-w-0" disabled={readOnly || busy} aria-labelledby={`${id}-legend`}>
        <legend className="flex items-center gap-2.5 text-sm font-medium">
          <span id={`${id}-legend`}>Darstellung</span>
          <HelpTip topic="Darstellung">{HELP_TIPS.doorPresentation}</HelpTip>
        </legend>
        <div className="mt-1 grid gap-2">
          {PRESENTATIONS.map(([value, label]) => (
            <div key={value} className="flex items-center gap-2">
              <input
                id={`${id}-${value}`}
                type="radio"
                name={`${id}-darstellung`}
                value={value}
                checked={draft.presentation === value}
                onChange={() => {
                  onChange({
                    ...draft,
                    presentation: value,
                    layout: fitLayout(draft.layout, value),
                  });
                }}
              />
              <label className="text-sm" htmlFor={`${id}-${value}`}>
                {label}
              </label>
            </div>
          ))}
        </div>
        <div
          className="mt-4 flex items-center gap-2"
          hidden={!sponsorWorkflow || (detail.customer_sponsor_id ?? null) !== null}
        >
          <input
            id={`${id}-sponsor`}
            className="border border-input"
            type="checkbox"
            checked={draft.sponsorEditable}
            onChange={(event) => {
              onChange({ ...draft, sponsorEditable: event.target.checked });
            }}
          />
          <label className="text-sm" htmlFor={`${id}-sponsor`}>
            Sponsoren dürfen den Aufbau ihres Türchens ändern
          </label>
          <HelpTip topic="Aufbau durch Sponsoren">{HELP_TIPS.sponsorLayout}</HelpTip>
        </div>
        {inBar || readOnly ? null : (
          <Button
            className="mt-4"
            type="button"
            disabled={!dirty}
            onClick={() => {
              void save();
            }}
          >
            Speichern
          </Button>
        )}
        <div role="status">
          {notice === null ? null : <p className="mt-2 text-sm text-destructive">{notice}</p>}
        </div>
      </fieldset>

      <div className="mt-6 flex items-center gap-2">
        <div role="group" aria-label="Türchen" className="inline-flex gap-1 rounded-md bg-muted p-1">
          {(
            [
              [false, "Belegtes Türchen"],
              [true, "Unbelegtes Türchen"],
            ] as const
          ).map(([value, name]) => (
            <button
              key={name}
              type="button"
              aria-pressed={unbooked === value}
              className={navLinkClassName({ isActive: unbooked === value })}
              onClick={() => {
                setUnbooked(value);
                setUnbookedSeen((seen) => seen || value);
              }}
            >
              {name}
            </button>
          ))}
        </div>
        <HelpTip topic="Unbelegtes Türchen">{HELP_TIPS.fallback}</HelpTip>
      </div>

      {/* The standard layout, on the example view's open door or a made-up one (D49: open,
          the configurator is the editor of this view). */}
      <div className="mt-2" hidden={unbooked}>
        <DoorEditor
          previewTitle="Vorschau des Türchen-Fensters"
          key={exampleOpen === null ? "erfunden" : "beispielansicht"}
          door={exampleOpen ?? SAMPLE_DOOR}
          layout={draft.layout}
          presentation={draft.presentation}
          layoutOnly
          {...(readOnly
            ? {}
            : {
                onLayoutChange: (layout: FullDoorLayout) => {
                  onChange({ ...draft, layout });
                },
              })}
        />
      </div>
      {unbookedSeen ? (
        <div ref={fallbackRef} className="mt-2" hidden={!unbooked}>
          <FallbackEditor
            detail={detail}
            layout={draft.layout}
            presentation={draft.presentation}
            readOnly={fallbackReadOnly}
            mayAcceptUnscanned={mayAcceptUnscanned}
            onSaved={onSaved}
            onRefused={showFallback}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The fallback content in the configurator (D88): one step with text and picture, in the
 * window as the draft of the standard layout shows it. The layout is set on the occupied
 * door only, so one view does not edit it from two places.
 */
function FallbackEditor({
  detail,
  layout,
  presentation,
  readOnly,
  mayAcceptUnscanned,
  onSaved,
  onRefused,
}: {
  readonly detail: ActionDetail;
  readonly layout: FullDoorLayout;
  readonly presentation: DoorPresentation;
  readonly readOnly: boolean;
  readonly mayAcceptUnscanned: boolean;
  readonly onSaved: () => void;
  /** The bar's save stopped at a check or the server refused: the editor may be hidden. */
  readonly onRefused: () => void;
}): ReactElement {
  const actionId = detail.action_id;
  // One copy per answer: the configurator takes every new copy as a stand saved elsewhere.
  const door = useMemo(() => fallbackDoor(detail), [detail]);
  // The page's reload, read when a write answers: its function is new on every render.
  const reload = useRef(onSaved);
  useEffect(() => {
    reload.current = onSaved;
  });
  const writes = useMemo<DoorWrites>(() => {
    const answer = (outcome: FallbackOutcome): DoorOutcome => {
      if (!outcome.ok) {
        onRefused();
        return outcome;
      }
      console.log("[DoorWindowSettings] fallback saved", actionId);
      reload.current();
      return { ok: true, booking: fallbackDoor(outcome.detail) };
    };
    return {
      // The text, and the picture's removal when the form let it go.
      save: async (request): Promise<DoorOutcome> => {
        const slide = request.slides?.[0];
        const saved = await saveFallbackText(actionId, slide?.body ?? "");
        const keep = (slide?.media ?? null) !== null;
        if (!saved.ok || keep || (saved.detail.fallback_image ?? null) === null) {
          return answer(saved);
        }
        return answer(await removeFallbackImage(actionId));
      },
      upload: async (upload): Promise<DoorOutcome> =>
        answer(await uploadFallbackImage(actionId, upload)),
      recut: async ({ assetId, crop }): Promise<DoorOutcome> =>
        answer(await uploadFallbackImage(actionId, { assetId, crop })),
      original: originalAddress,
      picture: assetAddress,
    };
  }, [actionId, onRefused]);
  return (
    <DoorEditor
      previewTitle="Vorschau des Ersatzinhalts"
      door={door}
      {...(readOnly ? {} : { writes })}
      onInvalid={onRefused}
      fallback
      saveLabel="Ersatzinhalt"
      videos={false}
      sponsored={false}
      hideLockedLayout
      acceptUnscanned={mayAcceptUnscanned}
      layout={layout}
      presentation={presentation}
    />
  );
}
